// A customer's recent bills and the sizes they usually buy, for the Fashion
// layout's customer panel. Same server queries Sales History uses
// (salesHistory, salesHistoryDetails), across all tills and dates; sizes come
// from the till's own product data, so nothing new is stored.
import { readVariantAxes } from "./styleIndex";

// How many recent bills are opened for their lines.
export const HISTORY_BILLS = 5;

const cleanTerm = (s) => String(s ?? "").replace(/["\\]/g, "").trim();

// The till's walk-in customer has no history worth showing.
export function isNamedCustomer(customer, tillData) {
  const id = customer?.cwrCustomerId;
  if (!id) return false;
  return id !== tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId;
}

// How many bills the history asks for.
export const BILL_LIMIT = 50;

// Every bill for this customer in any store and on any till, sales and
// returns. The server matches the customer id exactly, so it is searched first;
// the code or mobile is only used when the customer has no id yet.
export function buildCustomerBillsQuery(customer, limit = BILL_LIMIT) {
  const term = cleanTerm(customer?.cwrCustomerId || customer?.code || customer?.mobileNo);
  const sort = JSON.stringify({ dateordered: "desc", documentno: "desc" }).replace(/"/g, '\\"');
  return {
    query: `query {salesHistory(
      q: "${term}",
      filter_by: "{\\"DateRange\\":[null,null]}",
      startRow: "0",
      endRow: "${limit}",
      sort_by: "${sort}",
      tillId: null,
      layAway: "N"
    ) { sOrderID documentno dateordered orderTime grosstotal isReturn totalQty docstatus cSBunitID csBUnit { csBunitId name } cwrTill { till } cwrB2cCustomer { cwrCustomerId mobileNo } }}`,
  };
}

export function buildBillLinesQuery(orderId) {
  return {
    query: `query {salesHistoryDetails(orderId: "${cleanTerm(orderId)}") {
      sOrderID
      line { qty linegross product { mProductId name value } }
    }}`,
  };
}

const timeOf = (d) => {
  const t = Date.parse(d);
  return Number.isFinite(t) ? t : 0;
};

// Is this row someone else's? The server does not always send the customer id
// back; then the mobile number decides, and a row with neither is kept.
function otherCustomer(row, customer) {
  const rowId = row?.cwrB2cCustomer?.cwrCustomerId;
  if (rowId && customer?.cwrCustomerId) return rowId !== customer.cwrCustomerId;
  const rowMobile = mobileKey(row?.cwrB2cCustomer?.mobileNo);
  const mobile = mobileKey(customer?.mobileNo);
  return rowMobile.length >= 6 && mobile.length >= 6 && rowMobile !== mobile;
}

// Server rows → this customer's bills, newest first. A return's amount is
// shown as money going back (negative). store is the store name, or the till
// when the server sends no store.
export function readCustomerBills(response, customer) {
  const rows = response?.data?.data?.salesHistory;
  if (!Array.isArray(rows)) return null;
  return rows
    .filter((r) => r && !otherCustomer(r, customer))
    .map((r) => {
      const isReturn = r.isReturn === "Y" || r.isReturn === true;
      const total = Number(r.grosstotal) || 0;
      const items = Number(r.totalQty);
      return {
        id: r.sOrderID,
        documentno: r.documentno,
        date: r.dateordered || r.orderTime || "",
        total: isReturn && total > 0 ? -total : total,
        isReturn,
        items: Number.isFinite(items) ? Math.abs(items) : null,
        bunitId: r.cSBunitID || r.csBUnit?.csBunitId || null,
        store: r.csBUnit?.name || r.cwrTill?.till || "",
        till: r.cwrTill?.till || "",
      };
    })
    .sort((a, b) => timeOf(b.date) - timeOf(a.date));
}

// "All stores" or "This store". This store needs the server to say which store
// each bill is from; when it does not, the choice is not offered.
export const canScopeToStore = (bills) => Array.isArray(bills) && bills.some((b) => b.bunitId);

export const billsInScope = (bills, scope, bunitId) =>
  scope === "here" && bunitId ? (bills || []).filter((b) => b.bunitId === bunitId) : bills || [];

// Lifetime spend (returns take money off), sale bills, and the average sale.
export function billStats(bills) {
  const list = bills || [];
  const spend = list.reduce((a, b) => a + (Number(b.total) || 0), 0);
  const sales = list.filter((b) => !b.isReturn).length;
  return { spend, bills: sales, average: sales ? spend / sales : 0 };
}

export const readBillLines = (response) => {
  const lines = response?.data?.data?.salesHistoryDetails?.[0]?.line;
  return Array.isArray(lines) ? lines : [];
};

// Sizes bought, most often first: [{ size, qty, category }], from sale bills'
// lines, counted per category (a 32 in jeans and an M in shirts are both
// "usual"). productById looks up the till's synced product; products no
// longer on the till, or without a size, are left out.
export function sizeProfile(lines, productById) {
  const counts = new Map();
  (lines || []).forEach((l) => {
    const product = productById(l?.product?.mProductId);
    const size = product ? readVariantAxes(product).size : null;
    if (size == null) return;
    const category = String(product.productCategoryName || "").trim() || null;
    const key = `${category || ""}\u0000${size}`;
    const row = counts.get(key) || { size, qty: 0, category };
    row.qty += Number(l.qty) || 0;
    counts.set(key, row);
  });
  return [...counts.values()].filter((r) => r.qty > 0).sort((a, b) => b.qty - a.qty);
}

// One account across the store and the online store: the points and store
// credit the till loaded with the customer, and their online orders. Online
// orders are found by mobile number, since the channel may have made its own
// customer record for them.
const amountOrNull = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

export function accountBalances(customer) {
  return { points: amountOrNull(customer?.retlLoyaltyBalance), credit: amountOrNull(customer?.cwrWallet?.balance_Amt) };
}

// The last ten digits, so +91 98450 12345 and 9845012345 match.
export const mobileKey = (m) => String(m ?? "").replace(/\D/g, "").slice(-10);

// The order search also matches order numbers and names; keep only the
// customer's own orders.
export function customerOnlineOrders(rows, mobileNo) {
  const key = mobileKey(mobileNo);
  if (key.length < 6 || !Array.isArray(rows)) return [];
  return rows.filter((o) => mobileKey(o.mobileNo) === key);
}

// The one-line summary under the customer's name on the bill: tier and points.
export function customerSummary(customer) {
  const tier = String(customer?.loyaltyLevel?.name || "").trim();
  const { points } = accountBalances(customer);
  const parts = [];
  if (tier) parts.push(tier);
  if (points !== null) parts.push(`${Math.round(points).toLocaleString("en-IN")} pts`);
  return parts.join(" · ");
}
